/**
 * One person's timeline: their messages and the activities of every one of
 * their leads, merged in time order and grouped by India day.
 *
 * `ContactTimeline` loads it through `useContactTimeline`, newest page first,
 * and offers "Load earlier" while older pages exist. `TimelineView` draws a
 * list of entries it is given, which lead detail also uses for a lead with no
 * contact (that lead's own activities, nothing more).
 *
 * The view owns its scroll box. It opens at the newest entry, follows a new
 * entry at the bottom, and keeps the reader's place when an older page is
 * added at the top.
 */

import { Loader2 } from "lucide-react";
import { Fragment, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { useContactTimeline } from "@/features/realtor/hooks/use-realtor-crm";
import type { TimelineEntry as Entry } from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";
import { dayKey, dayLabel } from "../messages/message-format";
import { TimelineEntry } from "./timeline-entry";

interface SharedProps {
	contactName: string;
	/** Shown when the person has nothing logged. */
	emptyText: string;
	/** When given, activity rows offer a delete button. */
	onDeleteActivity?: (entry: Entry) => void;
	/** Sizing for the scroll box, e.g. `flex-1` or `max-h-[32rem]`. */
	className?: string;
}

export function ContactTimeline({
	contactId,
	...shared
}: SharedProps & { contactId: string }) {
	const timeline = useContactTimeline(contactId);
	// Pages arrive newest first, each oldest first inside.
	const entries = useMemo(
		() =>
			timeline.data
				? [...timeline.data.pages].reverse().flatMap((page) => page.entries)
				: undefined,
		[timeline.data],
	);

	return (
		<TimelineView
			{...shared}
			entries={entries}
			isPending={timeline.isPending}
			error={entries ? null : timeline.error}
			isRetrying={timeline.isFetching}
			onRetry={() => void timeline.refetch()}
			hasEarlier={timeline.hasNextPage}
			isLoadingEarlier={timeline.isFetchingNextPage}
			loadEarlierFailed={timeline.isFetchNextPageError}
			onLoadEarlier={() => void timeline.fetchNextPage()}
		/>
	);
}

export function TimelineView({
	entries,
	contactName,
	emptyText,
	onDeleteActivity,
	className,
	isPending = false,
	error = null,
	isRetrying = false,
	onRetry,
	hasEarlier = false,
	isLoadingEarlier = false,
	loadEarlierFailed = false,
	onLoadEarlier,
}: SharedProps & {
	/** Oldest first. Undefined until loaded. */
	entries: Entry[] | undefined;
	isPending?: boolean;
	error?: Error | null;
	isRetrying?: boolean;
	onRetry?: () => void;
	hasEarlier?: boolean;
	isLoadingEarlier?: boolean;
	loadEarlierFailed?: boolean;
	onLoadEarlier?: () => void;
}) {
	const { t, i18n } = useTranslation();
	const scroller = useRef<HTMLDivElement>(null);
	// Distance from the bottom, recorded when an older page is asked for.
	const keepFromBottom = useRef<number | null>(null);
	const newestId = useRef<string | null>(null);

	useLayoutEffect(() => {
		const node = scroller.current;
		if (!node || !entries) return;
		const newest = entries[entries.length - 1]?.id ?? null;
		if (keepFromBottom.current !== null) {
			node.scrollTop = node.scrollHeight - keepFromBottom.current;
			if (!isLoadingEarlier) keepFromBottom.current = null;
		} else if (newest !== newestId.current) {
			node.scrollTop = node.scrollHeight;
			// Below `md` the timeline has no fixed height and never overflows,
			// so the page scrolls instead: bring the newest entry into view.
			if (node.scrollHeight <= node.clientHeight && newestId.current === null) {
				node.lastElementChild?.scrollIntoView?.({ block: "end" });
			}
		}
		newestId.current = newest;
	}, [entries, isLoadingEarlier]);

	const loadEarlier = () => {
		const node = scroller.current;
		if (node) keepFromBottom.current = node.scrollHeight - node.scrollTop;
		onLoadEarlier?.();
	};

	return (
		<div
			ref={scroller}
			className={`min-h-[12rem] overflow-y-auto bg-dashboard-surface px-3 py-4 sm:px-4 ${className ?? ""}`}
			data-testid="contact-timeline"
		>
			{isPending && !error && (
				<div className="space-y-3" data-testid="contact-timeline-loading">
					<Skeleton className="h-12 w-2/3 bg-dashboard-border" />
					<Skeleton className="ml-auto h-12 w-2/3 bg-dashboard-border" />
					<Skeleton className="h-12 w-1/2 bg-dashboard-border" />
				</div>
			)}

			{error && (
				<QueryErrorState
					message={error.message || t("realtor.timeline.loadFailed")}
					onRetry={onRetry}
					isRetrying={isRetrying}
					className="py-6"
				/>
			)}

			{entries && hasEarlier && (
				<div className="mb-3 flex flex-col items-center gap-1">
					<StudioButton
						variant="secondary"
						size="xs"
						disabled={isLoadingEarlier}
						onClick={loadEarlier}
					>
						{isLoadingEarlier && <Loader2 className="h-3 w-3 animate-spin" />}
						{isLoadingEarlier
							? t("realtor.timeline.loadingEarlier")
							: t("realtor.timeline.loadEarlier")}
					</StudioButton>
					{loadEarlierFailed && !isLoadingEarlier && (
						<p role="alert" className="text-[11px] text-dashboard-pill-risk">
							{t("realtor.timeline.loadEarlierFailed")}
						</p>
					)}
				</div>
			)}

			{entries && entries.length === 0 && (
				<p className="mx-auto max-w-sm py-8 text-center text-sm text-dashboard-muted">
					{emptyText}
				</p>
			)}

			{entries && entries.length > 0 && (
				<ol className="space-y-2" aria-label={contactName}>
					{entries.map((entry, index) => {
						const previous = entries[index - 1];
						const newDay =
							!previous ||
							dayKey(previous.occurredAt) !== dayKey(entry.occurredAt);
						return (
							<Fragment key={`${entry.kind}-${entry.id}`}>
								{newDay && (
									<li
										className="flex items-center gap-3 py-2"
										data-testid="timeline-day-separator"
									>
										<span className="h-px flex-1 bg-dashboard-border" />
										<span className="text-[11px] font-medium uppercase tracking-wider text-dashboard-muted">
											{dayLabel(entry.occurredAt, i18n.language, {
												today: t("realtor.messages.today"),
												yesterday: t("realtor.messages.yesterday"),
											})}
										</span>
										<span className="h-px flex-1 bg-dashboard-border" />
									</li>
								)}
								<TimelineEntry
									entry={entry}
									contactName={contactName}
									onDeleteActivity={onDeleteActivity}
								/>
							</Fragment>
						);
					})}
				</ol>
			)}
		</div>
	);
}
